/**
 * The zen modal's "how this chat was opened" props (FRONT-1337).
 *
 * Each one describes a single event at the moment the caller mounted the modal
 * — the text typed in the launcher, the Work container the chat was minted
 * about, an auto-started voice call. They are consumed once, on open.
 *
 * When the "Chat activity" dropdown (`ChatActivityMenu`, FRONT-1579 —
 * formerly the SWITCH bar, FRONT-1337) hands the modal to a DIFFERENT
 * thread, every one of them has to be dropped. Replaying them would post the
 * first chat's opening message into a conversation the user merely hopped
 * to, or scope an unrelated thread to the project page they happened to
 * come from.
 *
 * Kept in its own module with a drift guard (see the test beside it) because
 * the failure mode is silent: add a new `initial…` prop later, forget it here,
 * and it leaks onto every switched-to thread with nothing to catch it.
 */
export const ONE_SHOT_OPEN_PROPS = [
  'initialMessage',
  'initialAttachments',
  'initialImage',
  'initialVoiceCall',
  'initialScope',
  // FRONT-776: the Folder equivalent of `initialScope` — same leak, same fix.
  'initialFolderScope',
  // FRONT-2433: a keep action the Chats page asked for on open — replaying
  // it onto a switched-to thread would save the wrong conversation.
  'initialAction',
] as const;

export type OneShotOpenProp = (typeof ONE_SHOT_OPEN_PROPS)[number];

/** Spread over the inner modal's props on a switch to clear all of them. */
export const CLEARED_ON_SWITCH: Record<OneShotOpenProp, undefined> =
  Object.fromEntries(ONE_SHOT_OPEN_PROPS.map((k) => [k, undefined])) as Record<
    OneShotOpenProp,
    undefined
  >;
